import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdOutlineAdd } from 'react-icons/md';
import { Button, SearchInput } from '@ds';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import {
  HOSPITAL_CONTACT_LIST_FILTERS_BASE,
  getRenderHospitalContactsTableCell,
  HOSPITAL_CONTACTS_TABLE_COLUMNS,
} from '@lp/features/admin/tables/HospitalContactListTable';
import { HospitalContactFilters } from '@lp/features/admin/tables/HospitalContactListTable/components/HospitalContactFilters';
import { AuthContext } from '@lp/core/auth';
import { HospitalsContactListFilters } from '@/api/apis/facilities';
import { useHospitalContactsListQuery } from '@/queries/hospital-contacts';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { useWrappedRemoveHospitalContactMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';

export const HospitalContactsList = () => {
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: HOSPITAL_CONTACT_LIST_FILTERS_BASE,
    query: useHospitalContactsListQuery,
  });

  const { resolveSubdivisionName } = useCountriesData();

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions =
    checkPermission(AdminPermissions.HospitalCreate) || checkPermission(AdminPermissions.OpoCreate);

  const {
    isOpen,
    isLoading: isDeletionLoading,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } = useEntityDelete({
    query: useWrappedRemoveHospitalContactMutation,
    successMessage: 'Donor Hospital contact deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/hospitals/contacts/create');
  };

  const handleEdit = useCallback(
    (contactId: string) => {
      navigate(`/admin/hospitals/contacts/${contactId}`);
    },
    [navigate],
  );

  const renderTableCell = useMemo(
    () => getRenderHospitalContactsTableCell(handleEdit, onModalOpen, resolveSubdivisionName),
    [handleEdit, onModalOpen, resolveSubdivisionName],
  );

  const TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions
      ? HOSPITAL_CONTACTS_TABLE_COLUMNS
      : HOSPITAL_CONTACTS_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="Donor Hospital Contacts">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <HospitalContactFilters<HospitalsContactListFilters>
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="Donor Hospital Contacts"
            columns={TABLE_COLUMNS_FILTERED}
            renderCell={renderTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete Donor Hospital Contact?"
        description="Are you sure you want to delete the Donor Hospital Contact?"
        size="lg"
        isOpen={isOpen}
        isLoading={isDeletionLoading}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
